/**
 * WordTooltip — attaches a global click listener to detect when the user
 * clicks on a word anywhere in the page and shows a floating dictionary card.
 *
 * Usage:
 *   <WordTooltip enabled onSaveWord={handleSave} />
 *
 * The tooltip renders via a portal into document.body so it's always on top.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth.store';
import { lookupWord, getWordAtPoint, type DictionaryResult } from './useDictionary';
import { readDictSettings, DICT_SETTINGS_KEY, type DictSettings } from './dictSettings';
import { ROUTES } from '@/router/routes';
import styles from './WordTooltip.module.css';

interface TooltipState {
  word: string;
  x: number;
  y: number;
  result: DictionaryResult | null;
  loading: boolean;
}

export interface SavedVocabWord {
  word: string;
  phonetic?: string;
  definition?: string;
  translation?: string;
  addedAt: number;
  sourceContext?: string; // optional surrounding sentence
}

interface Props {
  /** Whether the tooltip is active. Pass false to disable (e.g. during exams). */
  enabled?: boolean;
  /** Trigger mode (legacy — overridden by localStorage settings if present):
   *  'click'     — click on a word to look it up (default)
   *  'selection' — highlight a word with the mouse to look it up */
  mode?: 'click' | 'selection';
  /** Called when user clicks "Save to vocabulary" in the popup. */
  onSaveWord?: (entry: SavedVocabWord) => void;
}

// ── POS color class helper ──────────────────────────────────────────
function posClass(pos: string): string {
  if (pos.includes('verb')) return styles.posTagVerb ?? "";
  if (pos.includes('noun')) return styles.posTagNoun ?? "";
  if (pos.includes('adjective') || pos === 'adj') return styles.posTagAdj ?? "";
  if (pos.includes('adverb') || pos === 'adv') return styles.posTagAdv ?? "";
  return '';
}

// ── Speech Synthesis helper for instant pronunciation ───────────────
function speakEnglishWord(word: string) {
  if (typeof window === 'undefined' || !('speechSynthesis' in window)) return;
  try {
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(word);
    utterance.lang = 'en-US';
    utterance.rate = 0.9;
    const voices = window.speechSynthesis.getVoices();
    const enVoice = voices.find(v => v.lang.startsWith('en') && (v.name.includes('Google') || v.name.includes('Natural') || v.name.includes('Samantha') || v.name.includes('David') || v.name.includes('English')));
    if (enVoice) utterance.voice = enVoice;
    window.speechSynthesis.speak(utterance);
  } catch {}
}

// ── Position helpers ────────────────────────────────────────────────
const TOOLTIP_W = 300;
const TOOLTIP_H_APPROX = 220;
const MARGIN = 12;

function computePos(rawX: number, rawY: number): { left: number; top: number } {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  let left = rawX + 12;
  let top = rawY + 14;
  if (left + TOOLTIP_W + MARGIN > vw) left = rawX - TOOLTIP_W - 4;
  if (left < MARGIN) left = MARGIN;
  if (top + TOOLTIP_H_APPROX + MARGIN > vh) top = rawY - TOOLTIP_H_APPROX - 10;
  if (top < MARGIN) top = MARGIN;
  return { left, top };
}

// ── Tooltip card component ──────────────────────────────────────────
function TooltipCard({
  state, onClose, onSave, onGoVocab,
  showTranslation = true, showDefinitions = true, showPhonetics = true,
}: {
  state: TooltipState;
  onClose: () => void;
  onSave: (w: string, result: DictionaryResult | null) => void;
  onGoVocab: () => void;
  showTranslation?: boolean;
  showDefinitions?: boolean;
  showPhonetics?: boolean;
}) {
  const [saved, setSaved] = useState(false);
  const [isPlayingAudio, setIsPlayingAudio] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const { x, y, word, result, loading } = state;
  const { left, top } = computePos(x, y);

  function handleSave() {
    onSave(word, result);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }

  function playAudio() {
    setIsPlayingAudio(true);
    setTimeout(() => setIsPlayingAudio(false), 1200);

    if (result?.audioUrl) {
      try {
        if (!audioRef.current) {
          audioRef.current = new Audio(result.audioUrl);
        } else {
          audioRef.current.src = result.audioUrl;
        }
        audioRef.current.play().catch(() => {
          // Audio file failed (e.g. 404 or CORS) -> fallback to SpeechSynthesis
          speakEnglishWord(word);
        });
        return;
      } catch {
        speakEnglishWord(word);
        return;
      }
    }

    // No MP3 URL -> directly speak via Web Speech API
    speakEnglishWord(word);
  }

  return (
    <div
      className={styles.tooltip}
      style={{ left, top }}
      onMouseDown={e => e.stopPropagation()}
      onClick={e => e.stopPropagation()}
    >
      {/* ── Header ── */}
      <div className={styles.header}>
        <div className={styles.wordBlock}>
          <div className={styles.word}>{word}</div>
          {showPhonetics && result?.phonetic && <div className={styles.phonetic}>{result.phonetic}</div>}
        </div>
        <div className={styles.headerRight}>
          <button
            className={`${styles.audioBtn} ${isPlayingAudio ? styles.audioBtnPlaying : ''}`}
            onClick={playAudio}
            title="Озвучить слово"
            type="button"
          >
            🔊
          </button>
          <button
            className={`${styles.saveBtn} ${saved ? styles.saveBtnDone : ''}`}
            onClick={handleSave}
            type="button"
          >
            {saved ? '✓ Сохранено' : '＋ В словарь'}
          </button>
          <button className={styles.closeBtn} onClick={onClose} type="button">✕</button>
        </div>
      </div>

      {/* ── Body ── */}
      {loading ? (
        <div className={styles.loading}>
          <div className={styles.spinner} />
          Поиск перевода…
        </div>
      ) : !result ? (
        <div className={styles.empty}>Определение не найдено</div>
      ) : (
        <div className={styles.body}>
          {/* Russian translation */}
          {showTranslation && result.translation && (
            <div className={styles.translation}>
              <span className={styles.translationFlag}>🇷🇺</span>
              <span className={styles.translationText}>{result.translation}</span>
            </div>
          )}

          {/* English definitions */}
          {showDefinitions && result.definitions.length > 0 && (
            <div className={styles.definitions}>
              {result.definitions.slice(0, 3).map((def, i) => (
                <div key={i}>
                  <div className={styles.definition}>
                    <span className={`${styles.posTag} ${posClass(def.partOfSpeech)}`}>
                      {def.partOfSpeech || 'word'}
                    </span>
                    <span>{def.definition}</span>
                  </div>
                  {def.example && (
                    <div className={styles.example}>"{def.example}"</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Footer ── */}
      <div className={styles.footer}>
        <span className={styles.footerLabel}>📖 Oxford / MyMemory / Wiktionary</span>
        <span className={styles.vocabLink} onClick={onGoVocab}>
          Мой словарь →
        </span>
      </div>
    </div>
  );
}

// ── Shared skip-check helper ────────────────────────────────────────
function shouldSkipTarget(target: HTMLElement): boolean {
  if (target.closest('[data-dict-tooltip]')) return true;
  if (target.closest('button, input, textarea, select, option, label, [role="button"], [role="tab"], [role="menuitem"], [role="dialog"], [role="switch"]')) return true;
  if (target.closest('svg, path, canvas, video, audio, iframe')) return true;
  const editableEl = target.closest<HTMLElement>('[contenteditable]');
  if (editableEl && editableEl.contentEditable === 'true') return true;
  if (target.closest('[data-exercise-answer], [data-no-dict]')) return true;
  return false;
}

// ── Main WordTooltip component ──────────────────────────────────────
export default function WordTooltip({ enabled = true, mode: modeProp = 'click', onSaveWord }: Props) {
  const navigate = useNavigate();
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);
  const { user } = useAuthStore();
  const userId = user?.id || 'guest';

  // Live settings from localStorage — updated instantly when student/teacher toggles
  const [settings, setSettings] = useState<DictSettings>(() => readDictSettings());
  useEffect(() => {
    const syncSettings = () => setSettings(readDictSettings());
    syncSettings();
    const onStorage = (e: StorageEvent) => {
      if (e.key === DICT_SETTINGS_KEY || !e.key) syncSettings();
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener('focus', syncSettings);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('focus', syncSettings);
    };
  }, []);

  // Derive effective trigger from settings (settings win over prop)
  const effectiveTrigger = settings.trigger; // 'click' | 'selection' | 'off'
  const isActive = enabled && effectiveTrigger !== 'off';

  const clearHighlight = useCallback(() => {
    try {
      window.getSelection()?.removeAllRanges();
    } catch {}
  }, []);

  const close = useCallback(() => {
    setTooltip(null);
    clearHighlight();
  }, [clearHighlight]);

  const showWord = useCallback(async (word: string, x: number, y: number) => {
    setTooltip({ word, x, y, result: null, loading: true });
    const result = await lookupWord(word);
    setTooltip(prev =>
      prev && prev.word === word
        ? { ...prev, result, loading: false }
        : prev
    );
    // autoSave: silently add to vocabulary on lookup
    if (result && settings.autoSave) {
      try {
        const entry: SavedVocabWord = {
          word, phonetic: result.phonetic,
          definition: result.definitions[0]?.definition,
          translation: result.translation, addedAt: Date.now(),
        };
        const STORAGE_KEY = `ep_vocabulary_${userId}`;
        const raw = localStorage.getItem(STORAGE_KEY) ?? '[]';
        const list: SavedVocabWord[] = JSON.parse(raw);
        if (!list.find(w => w.word.toLowerCase() === word.toLowerCase())) {
          list.unshift(entry); localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
        }
      } catch {}
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.autoSave, userId]);

  // ── CLICK mode ──────────────────────────────────────────────────
  useEffect(() => {
    if (!isActive || effectiveTrigger !== 'click') return;

    const handleClick = async (e: MouseEvent) => {
      if (shouldSkipTarget(e.target as HTMLElement)) return;

      const linkEl = (e.target as HTMLElement).closest('a');
      if (linkEl) {
        const href = linkEl.getAttribute('href');
        if (!href || href === '#') e.preventDefault();
      }

      const match = getWordAtPoint(e.clientX, e.clientY);
      if (!match) {
        // If clicking in empty space, close any open tooltip & clear selection
        setTooltip(null);
        clearHighlight();
        return;
      }

      // Highlight the clicked word
      try {
        const sel = window.getSelection();
        if (sel) {
          sel.removeAllRanges();
          sel.addRange(match.range);
        }
      } catch {}

      await showWord(match.word, e.clientX, e.clientY);
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        close();
      }
    };

    document.addEventListener('click', handleClick, true);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('click', handleClick, true);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isActive, effectiveTrigger, showWord, close, clearHighlight]);

  // ── SELECTION mode ──────────────────────────────────────────────
  useEffect(() => {
    if (!isActive || effectiveTrigger !== 'selection') return;

    const handleMouseUp = async (e: MouseEvent) => {
      await new Promise(r => setTimeout(r, 40));
      if (shouldSkipTarget(e.target as HTMLElement)) return;

      const sel = window.getSelection();
      if (!sel || sel.isCollapsed || !sel.rangeCount) return;

      const rawText = sel.toString().trim();
      if (!rawText) return;

      // Extract clean words from selection
      const wordsInSel = rawText.split(/\s+/).map(w => w.replace(/[^a-zA-Z'\-]/g, '')).filter(Boolean);
      if (wordsInSel.length === 0) return;

      const targetWord = wordsInSel.length <= 4 ? wordsInSel.join(' ') : wordsInSel[0];
      if (!targetWord || targetWord.length < 2) return;

      const range = sel.getRangeAt(0);
      const rect = range.getBoundingClientRect();
      const x = (rect.left + rect.right) / 2;
      const y = rect.bottom;

      await showWord(targetWord, x, y);
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        close();
      }
    };

    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isActive, effectiveTrigger, showWord, close]);

  const handleSave = useCallback(
    (word: string, result: DictionaryResult | null) => {
      const entry: SavedVocabWord = {
        word,
        phonetic: result?.phonetic,
        definition: result?.definitions[0]?.definition,
        translation: result?.translation,
        addedAt: Date.now(),
      };
      try {
        const STORAGE_KEY = `ep_vocabulary_${userId}`;
        const raw = localStorage.getItem(STORAGE_KEY) ?? '[]';
        const list: SavedVocabWord[] = JSON.parse(raw);
        if (!list.find(w => w.word.toLowerCase() === word.toLowerCase())) {
          list.unshift(entry);
          localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
        }
      } catch {}
      onSaveWord?.(entry);
    },
    [onSaveWord, userId]
  );

  if (!tooltip) return null;

  return createPortal(
    <div data-dict-tooltip>
      <TooltipCard
        state={tooltip}
        onClose={close}
        onSave={handleSave}
        onGoVocab={() => {
          if (window.location.pathname.includes('/online')) {
            window.open(ROUTES.VOCABULARY, '_blank');
          } else {
            navigate(ROUTES.VOCABULARY);
          }
          close();
        }}
        showTranslation={settings.showTranslation}
        showDefinitions={settings.showDefinitions}
        showPhonetics={settings.showPhonetics}
      />
    </div>,
    document.body
  );
}

